import { useState, useEffect } from 'react'
import { Pencil, Trash2, Plus, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/shadcn/button'
import { Input } from '@/components/shadcn/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/shadcn/card'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip'

interface Address {
  street?: string | null
  number?: string | null
  extraAddressLine?: string | null
  postalCode?: string | null
  town?: string | null
  country?: string | null
}

interface CompanyInfoCardProps {
  title: string
  name?: string | null
  phoneNumber?: string | null
  taxId?: string | null
  invoiceEmails?: string[] | null
  address?: Address | null
  showAddress?: boolean
  showSepaInstruction?: boolean
  canEdit?: boolean
  onEdit?: () => void
  canDelete?: boolean
  onDelete?: () => void
  /** When provided, the email list becomes inline-editable with a save button. */
  onSaveEmails?: (emails: string[]) => Promise<unknown>
  isSavingEmails?: boolean
}

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex items-start gap-4 py-2 border-b last:border-0">
      <span className="text-sm text-muted-foreground w-48 shrink-0">{label}</span>
      <span className="text-sm">{value || '—'}</span>
    </div>
  )
}

export function CompanyInfoCard({
                                  title,
                                  name,
                                  phoneNumber,
                                  taxId,
                                  invoiceEmails,
                                  address,
                                  showAddress = true,
                                  showSepaInstruction = false,
                                  canEdit = false,
                                  onEdit,
                                  canDelete = false,
                                  onDelete,
                                  onSaveEmails,
                                  isSavingEmails = false,
                                }: CompanyInfoCardProps) {
  const { t } = useTranslation(['company', 'common'])

  // Local email state for inline editing — only used when onSaveEmails is provided
  const [emails, setEmails] = useState<string[]>(invoiceEmails ?? [])
  const isDirty = JSON.stringify(emails) !== JSON.stringify(invoiceEmails ?? [])

  // Sync when prop changes (e.g. after save invalidates query)
  useEffect(() => {
    setEmails(invoiceEmails ?? [])
  }, [invoiceEmails])

  const handleAddEmail = () => setEmails([...emails, ''])

  const handleChangeEmail = (index: number, value: string) => {
    setEmails(emails.map((e, i) => (i === index ? value : e)))
  }

  const handleRemoveEmail = (index: number) => {
    if (emails.length <= 1) return
    setEmails(emails.filter((_, i) => i !== index))
  }

  const handleSave = async () => {
    const trimmed = emails.map(e => e.trim()).filter(e => e !== '')
    if (trimmed.length === 0) return
    await onSaveEmails?.(trimmed)
  }

  return (
    <Card className="flex-1 min-w-[300px]">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{title}</CardTitle>
          <div className="flex items-center gap-1">
            {canEdit && onEdit && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" onClick={onEdit}>
                    <Pencil className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('buttons.edit', { ns: 'common' })}</TooltipContent>
              </Tooltip>
            )}
            {canDelete && onDelete && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" onClick={onDelete}>
                    <Trash2 className="size-4 text-destructive" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('buttons.delete', { ns: 'common' })}</TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {name !== undefined && <InfoRow label={t('infoCard.fields.company')} value={name} />}
        {phoneNumber !== undefined && <InfoRow label={t('infoCard.fields.phoneNumber')} value={phoneNumber} />}
        {taxId !== undefined && <InfoRow label={t('infoCard.fields.taxId')} value={taxId} />}

        {/* Inline editable emails when onSaveEmails is provided, otherwise read-only */}
        {invoiceEmails !== undefined && (
          onSaveEmails ? (
            <div className="flex items-start gap-4 py-2 border-b">
              <span className="text-sm text-muted-foreground w-48 shrink-0 pt-2">
                {t('infoCard.fields.invoiceEmail')}
              </span>
              <div className="flex flex-col gap-2 flex-1">
                {emails.map((email, i) => (
                  <div key={i} className="flex gap-2">
                    <Input
                      value={email}
                      onChange={(e) => handleChangeEmail(i, e.target.value)}
                      placeholder="name@beispiel.de"
                      className="h-8 text-sm"
                    />
                    {emails.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-8 shrink-0"
                        onClick={() => handleRemoveEmail(i)}
                      >
                        <Trash2 className="size-3.5 text-destructive" />
                      </Button>
                    )}
                  </div>
                ))}
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-2 h-8 text-xs"
                    onClick={handleAddEmail}
                  >
                    <Plus className="size-3.5" />
                    {t('infoCard.fields.addEmail')}
                  </Button>
                  {isDirty && (
                    <Button
                      type="button"
                      size="sm"
                      className="h-8 text-xs"
                      onClick={handleSave}
                      disabled={isSavingEmails}
                    >
                      {isSavingEmails
                        ? <Loader2 className="size-3.5 animate-spin" />
                        : t('buttons.save', { ns: 'common' })}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            invoiceEmails?.map((email, i) => (
              <InfoRow key={i} label={i === 0 ? t('infoCard.fields.invoiceEmail') : ''} value={email} />
            ))
          )
        )}

        {showAddress && address && (
          <>
            <InfoRow label={t('infoCard.fields.streetNumber')} value={[address.street, address.number].filter(Boolean).join(' ')} />
            <InfoRow label={t('infoCard.fields.extraAddressLine')} value={address.extraAddressLine} />
            <InfoRow label={t('infoCard.fields.postalCode')} value={address.postalCode} />
            <InfoRow label={t('infoCard.fields.town')} value={address.town} />
            <InfoRow label={t('infoCard.fields.country')} value={address.country} />
          </>
        )}
        <div className='flex flex-row mt-5 gap-6 justify-left'>
          {showSepaInstruction && (
            <div className="rounded-md bg-muted/30 border p-3 text-sm text-muted-foreground">
              <p>{t('infoCard.sepaInstruction')}</p>
              <p>
                Link: {' '}
                <a
                  href="https://bonisoft.de/anleitung-sepa-mandat/"
                  target="_blank"
                  rel="noreferrer"
                  className="underline text-primary"
                >
                  {t('infoCard.sepaLinkText')}
                </a>
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
